import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Trophy, Clock, CheckCircle2, XCircle, AlertCircle, Share2, RotateCcw, Home, Save, Loader2, MinusCircle, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { QuizData } from "@/types/quiz";
import Header from "@/components/Header";
import ShareResultDialog from "@/components/ShareResultDialog";

interface ResultsData {
  quizData: QuizData;
  answers: any[];
  score: number;
  correctAnswers: number;
  wrongAnswers: number;
  unanswered: number;
  timeTaken: number;
  isShared?: boolean;
  shareToken?: string;
  guestName?: string;
}

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [quizSaved, setQuizSaved] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const resultsData = location.state as ResultsData | undefined;
  const isShared = resultsData?.isShared;
  const shareToken = resultsData?.shareToken;
  const guestName = resultsData?.guestName;

  useEffect(() => {
    if (!resultsData) {
      toast({
        title: "No results found",
        description: "Please take a quiz first",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    // Auto-save for shared quizzes
    if (isShared && shareToken) {
      handleSaveSharedAttempt();
    }
  }, [resultsData, navigate, toast]);

  if (!resultsData) return null;

  const { quizData, answers, score, correctAnswers, wrongAnswers, unanswered, timeTaken } = resultsData;
  const percentage = ((correctAnswers / quizData.questions.length) * 100).toFixed(1);

  // Categorize questions
  const categorizedQuestions = useMemo(() => {
    const wrong: { question: typeof quizData.questions[0]; index: number; userAnswer: any }[] = [];
    const skipped: { question: typeof quizData.questions[0]; index: number; userAnswer: any }[] = [];
    const correct: { question: typeof quizData.questions[0]; index: number; userAnswer: any }[] = [];

    quizData.questions.forEach((question, index) => {
      const userAnswer = answers[index];
      if (userAnswer.selectedOption === null) {
        skipped.push({ question, index, userAnswer });
      } else if (userAnswer.isCorrect) {
        correct.push({ question, index, userAnswer });
      } else {
        wrong.push({ question, index, userAnswer });
      }
    });

    return { wrong, skipped, correct };
  }, [quizData.questions, answers]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSaveSharedAttempt = async () => {
    if (!shareToken) return;

    try {
      // Get the quiz_id from shared_quizzes
      const { data: sharedQuiz, error: sharedError } = await supabase
        .from("shared_quizzes")
        .select("quiz_id, id")
        .eq("share_token", shareToken)
        .single();

      if (sharedError) throw sharedError;

      // Save the attempt as guest
      const { error: attemptError } = await supabase
        .from("quiz_attempts")
        .insert({
          quiz_id: sharedQuiz.quiz_id,
          user_id: null,
          score,
          total_questions: quizData.questions.length,
          correct_answers: correctAnswers,
          wrong_answers: wrongAnswers,
          unanswered,
          time_taken_seconds: timeTaken,
          answers_data: answers as any,
          shared_quiz_token: shareToken,
          guest_name: guestName,
        });

      if (attemptError) throw attemptError;

      // Increment attempt count
      const { data: currentShared } = await supabase
        .from("shared_quizzes")
        .select("attempt_count")
        .eq("id", sharedQuiz.id)
        .single();

      if (currentShared) {
        await supabase
          .from("shared_quizzes")
          .update({ 
            attempt_count: (currentShared.attempt_count || 0) + 1
          })
          .eq("id", sharedQuiz.id);
      }

    } catch (error: any) {
      console.error("Error saving shared attempt:", error);
    }
  };

  const handleSaveQuiz = async () => {
    // Don't allow save for shared quizzes - already auto-saved
    if (isShared) {
      toast({
        title: "Already Saved",
        description: "Your attempt has been saved automatically",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to save your quiz",
      });
      navigate("/auth");
      return;
    }

    setIsSaving(true);

    try {
      // First, save the quiz
      const { data: quiz, error: quizError } = await supabase
        .from("quizzes")
        .insert([{
          user_id: user.id,
          topic: quizData.settings.topic,
          difficulty: quizData.settings.difficulty,
          total_questions: quizData.settings.totalQuestions,
          timer_minutes: quizData.settings.timerMinutes,
          negative_marking: quizData.settings.negativeMarking,
          penalty: quizData.settings.penalty,
          questions_data: quizData.questions as any,
        }])
        .select()
        .single();

      if (quizError) throw quizError;

      // Then save the attempt
      const { error: attemptError } = await supabase
        .from("quiz_attempts")
        .insert([{
          quiz_id: quiz.id,
          user_id: user.id,
          score,
          total_questions: quizData.questions.length,
          correct_answers: correctAnswers,
          wrong_answers: wrongAnswers,
          unanswered,
          time_taken_seconds: timeTaken,
          answers_data: answers as any,
        }]);

      if (attemptError) throw attemptError;

      setQuizSaved(true);
      toast({
        title: "Quiz Saved!",
        description: "Your results have been saved to your dashboard",
      });
    } catch (error: any) {
      console.error("Error saving quiz:", error);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetake = () => {
    navigate("/", { replace: true });
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 sm:pt-24 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          {/* Results Summary */}
          <div className="bg-gradient-card backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-card border border-border/50 mb-6 sm:mb-8">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-primary mb-3 sm:mb-4">
                <Trophy className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Quiz Complete!</h1>
              <p className="text-sm sm:text-base text-muted-foreground">{quizData.settings.topic}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
              <Card className="p-3 sm:p-4 md:p-6 text-center bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 card-interactive">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-0.5 sm:mb-1">
                  {percentage}%
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Final Score</div>
              </Card>

              <Card className="p-3 sm:p-4 md:p-6 text-center bg-gradient-to-br from-success/10 to-success/5 border-success/20 card-interactive">
                <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5 sm:mb-1">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
                  <div className="text-2xl sm:text-3xl font-bold text-success">{correctAnswers}</div>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Correct</div>
              </Card>

              <Card className="p-3 sm:p-4 md:p-6 text-center bg-gradient-to-br from-destructive/10 to-destructive/5 border-destructive/20 card-interactive">
                <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5 sm:mb-1">
                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-destructive" />
                  <div className="text-2xl sm:text-3xl font-bold text-destructive">{wrongAnswers}</div>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Wrong</div>
              </Card>

              <Card className="p-3 sm:p-4 md:p-6 text-center bg-gradient-to-br from-muted/10 to-muted/5 border-border card-interactive">
                <div className="flex items-center justify-center space-x-1 sm:space-x-2 mb-0.5 sm:mb-1">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                  <div className="text-2xl sm:text-3xl font-bold">{formatTime(timeTaken)}</div>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Time Taken</div>
              </Card>
            </div>

            {/* Quiz Settings Info */}
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mb-4 sm:mb-6">
              <Badge variant="outline" className="rounded-full px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 text-xs">
                <Trophy className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                {quizData.settings.difficulty}
              </Badge>
              <Badge variant="outline" className="rounded-full px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 text-xs">
                <AlertCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                {quizData.questions.length} Questions
              </Badge>
              {quizData.settings.negativeMarking && (
                <Badge variant="destructive" className="rounded-full px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 text-xs">
                  <XCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                  Penalty: {quizData.settings.penalty}
                </Badge>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
              {!isShared && user && !quizSaved && (
                <Button
                  onClick={handleSaveQuiz}
                  disabled={isSaving}
                  className="rounded-full bg-gradient-primary px-4 sm:px-6 md:px-8 text-xs sm:text-sm hover-scale"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                      <span className="hidden sm:inline">Saving...</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  ) : (
                    <>
                      <Save className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Save Quiz</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  )}
                </Button>
              )}
              
              {isShared && (
                <div className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 bg-success/10 text-success rounded-full border border-success/20">
                  <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium truncate">Saved as {guestName}</span>
                </div>
              )}
              
              {!user && !isShared && (
                <Button
                  onClick={() => navigate("/auth")}
                  className="rounded-full bg-gradient-primary px-4 sm:px-6 md:px-8 text-xs sm:text-sm hover-scale"
                >
                  <Save className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Login to Save</span>
                  <span className="sm:hidden">Login</span>
                </Button>
              )}

              <Button
                variant="outline"
                onClick={handleRetake}
                className="rounded-full px-4 sm:px-6 md:px-8 text-xs sm:text-sm hover-scale"
              >
                <RotateCcw className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">New Quiz</span>
                <span className="sm:hidden">New</span>
              </Button>

              <Button
                variant="outline"
                onClick={handleShare}
                className="rounded-full px-4 sm:px-6 md:px-8 text-xs sm:text-sm hover-scale"
              >
                <Share2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Share Quiz</span>
                <span className="sm:hidden">Share</span>
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="rounded-full px-4 sm:px-6 md:px-8 text-xs sm:text-sm hover-scale"
              >
                <Home className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                Home
              </Button>
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold">Detailed Analysis</h2>
            </div>

            <Accordion type="multiple" className="space-y-3">
              {/* 1. Review Wrong Answers Explanation */}
              <AccordionItem value="wrong-answers" className="border-0">
                <AccordionTrigger className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-xl hover:no-underline hover:bg-destructive/15 transition-colors data-[state=open]:rounded-b-none">
                  <div className="flex items-center space-x-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <span className="text-base sm:text-lg font-bold text-destructive">
                      Review Wrong Answers Explanation ({categorizedQuestions.wrong.length})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-0">
                  <div className="space-y-4 pt-4 pb-2 px-1">
                    {categorizedQuestions.wrong.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-success" />
                        <p className="text-lg font-medium">Great job!</p>
                        <p className="text-sm">You didn't get any wrong answers.</p>
                      </div>
                    ) : (
                      categorizedQuestions.wrong.map(({ question, index, userAnswer }) => {
                        const selectedOption = userAnswer.selectedOption;
                        const correctOption = question.correct_answer;

                        return (
                          <Card key={question.id} className="p-4 sm:p-6 bg-gradient-card backdrop-blur-sm border-destructive/30 card-interactive border-2">
                            <div className="flex-1">
                              <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2">
                                <Badge variant="destructive" className="text-xs">
                                  Question {index + 1}
                                </Badge>
                                <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-destructive" />
                              </div>
                              <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4">{question.question}</h3>

                              <div className="space-y-2 mb-4">
                                {question.options.map((option, optIdx) => {
                                  const isSelected = selectedOption === optIdx;
                                  const isCorrectOption = correctOption === optIdx;
                                  const optionLabel = String.fromCharCode(65 + optIdx);

                                  return (
                                    <div
                                      key={optIdx}
                                      className={`p-3 rounded-xl border-2 ${
                                        isCorrectOption
                                          ? "border-success bg-success/10"
                                          : isSelected
                                          ? "border-destructive bg-destructive/10"
                                          : "border-border"
                                      }`}
                                    >
                                      <div className="flex items-start space-x-3">
                                        <div
                                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                                            isCorrectOption
                                              ? "bg-success text-white"
                                              : isSelected
                                              ? "bg-destructive text-white"
                                              : "bg-muted text-muted-foreground"
                                          }`}
                                        >
                                          {optionLabel}
                                        </div>
                                        <div className="flex-1">
                                          <span className={isCorrectOption || isSelected ? "font-medium" : ""}>
                                            {option}
                                          </span>
                                          {isSelected && (
                                            <span className="ml-2 text-sm text-muted-foreground">(Your answer)</span>
                                          )}
                                          {isCorrectOption && (
                                            <span className="ml-2 text-sm text-success font-medium">✓ Correct</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <Separator className="my-4" />

                              <div className="bg-muted/30 rounded-xl p-4">
                                <h4 className="font-semibold mb-2 flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-primary mr-2"></span>
                                  Explanation:
                                </h4>
                                <p className="text-sm leading-relaxed">{question.explanation}</p>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 2. Not Answered Explanation */}
              <AccordionItem value="skipped-questions" className="border-0">
                <AccordionTrigger className="px-4 py-3 bg-warning/10 border border-warning/20 rounded-xl hover:no-underline hover:bg-warning/15 transition-colors data-[state=open]:rounded-b-none">
                  <div className="flex items-center space-x-2">
                    <MinusCircle className="h-5 w-5 text-warning" />
                    <span className="text-base sm:text-lg font-bold text-warning">
                      Not Answered Explanation ({categorizedQuestions.skipped.length})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-0">
                  <div className="space-y-4 pt-4 pb-2 px-1">
                    {categorizedQuestions.skipped.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-success" />
                        <p className="text-lg font-medium">Well done!</p>
                        <p className="text-sm">You answered all questions.</p>
                      </div>
                    ) : (
                      categorizedQuestions.skipped.map(({ question, index }) => {
                        const correctOption = question.correct_answer;

                        return (
                          <Card key={question.id} className="p-4 sm:p-6 bg-gradient-card backdrop-blur-sm border-warning/30 card-interactive border-2">
                            <div className="flex-1">
                              <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2">
                                <Badge variant="secondary" className="text-xs bg-warning/20 text-warning">
                                  Question {index + 1}
                                </Badge>
                                <MinusCircle className="h-4 w-4 sm:h-5 sm:w-5 text-warning" />
                                <span className="text-xs text-muted-foreground">(Not Answered)</span>
                              </div>
                              <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4">{question.question}</h3>

                              <div className="space-y-2 mb-4">
                                {question.options.map((option, optIdx) => {
                                  const isCorrectOption = correctOption === optIdx;
                                  const optionLabel = String.fromCharCode(65 + optIdx);

                                  return (
                                    <div
                                      key={optIdx}
                                      className={`p-3 rounded-xl border-2 ${
                                        isCorrectOption
                                          ? "border-success bg-success/10"
                                          : "border-border"
                                      }`}
                                    >
                                      <div className="flex items-start space-x-3">
                                        <div
                                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                                            isCorrectOption
                                              ? "bg-success text-white"
                                              : "bg-muted text-muted-foreground"
                                          }`}
                                        >
                                          {optionLabel}
                                        </div>
                                        <div className="flex-1">
                                          <span className={isCorrectOption ? "font-medium" : ""}>
                                            {option}
                                          </span>
                                          {isCorrectOption && (
                                            <span className="ml-2 text-sm text-success font-medium">✓ Correct Answer</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <Separator className="my-4" />

                              <div className="bg-muted/30 rounded-xl p-4">
                                <h4 className="font-semibold mb-2 flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-primary mr-2"></span>
                                  Explanation:
                                </h4>
                                <p className="text-sm leading-relaxed">{question.explanation}</p>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 3. Correct Answers Explanation */}
              <AccordionItem value="correct-answers" className="border-0">
                <AccordionTrigger className="px-4 py-3 bg-success/10 border border-success/20 rounded-xl hover:no-underline hover:bg-success/15 transition-colors data-[state=open]:rounded-b-none">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-success" />
                    <span className="text-base sm:text-lg font-bold text-success">
                      Correct Answers Explanation ({categorizedQuestions.correct.length})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0 pb-0">
                  <div className="space-y-4 pt-4 pb-2 px-1">
                    {categorizedQuestions.correct.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <AlertCircle className="h-12 w-12 mx-auto mb-3 text-warning" />
                        <p className="text-lg font-medium">Keep practicing!</p>
                        <p className="text-sm">No correct answers this time.</p>
                      </div>
                    ) : (
                      categorizedQuestions.correct.map(({ question, index, userAnswer }) => {
                        const selectedOption = userAnswer.selectedOption;
                        const correctOption = question.correct_answer;

                        return (
                          <Card key={question.id} className="p-4 sm:p-6 bg-gradient-card backdrop-blur-sm border-success/30 card-interactive border-2">
                            <div className="flex-1">
                              <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2">
                                <Badge variant="default" className="text-xs bg-success">
                                  Question {index + 1}
                                </Badge>
                                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
                              </div>
                              <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-3 sm:mb-4">{question.question}</h3>

                              <div className="space-y-2 mb-4">
                                {question.options.map((option, optIdx) => {
                                  const isCorrectOption = correctOption === optIdx;
                                  const optionLabel = String.fromCharCode(65 + optIdx);

                                  return (
                                    <div
                                      key={optIdx}
                                      className={`p-3 rounded-xl border-2 ${
                                        isCorrectOption
                                          ? "border-success bg-success/10"
                                          : "border-border"
                                      }`}
                                    >
                                      <div className="flex items-start space-x-3">
                                        <div
                                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${
                                            isCorrectOption
                                              ? "bg-success text-white"
                                              : "bg-muted text-muted-foreground"
                                          }`}
                                        >
                                          {optionLabel}
                                        </div>
                                        <div className="flex-1">
                                          <span className={isCorrectOption ? "font-medium" : ""}>
                                            {option}
                                          </span>
                                          {isCorrectOption && (
                                            <span className="ml-2 text-sm text-success font-medium">✓ Correct</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <Separator className="my-4" />

                              <div className="bg-muted/30 rounded-xl p-4">
                                <h4 className="font-semibold mb-2 flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-primary mr-2"></span>
                                  Explanation:
                                </h4>
                                <p className="text-sm leading-relaxed">{question.explanation}</p>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </main>

      <ShareResultDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        quizData={quizData}
        answers={answers}
        score={score}
        correctAnswers={correctAnswers}
        wrongAnswers={wrongAnswers}
        unanswered={unanswered}
        timeTaken={timeTaken}
        userId={user?.id}
      />
    </div>
  );
};

export default Results;
